<template>
  <v-card v-if="employee">
    <v-subheader
      v-if="employee.instructionsHistory && employee.instructionsHistory.length"
      class="subtitle mt-2 text-uppercase"
      >{{ $t("instructions.title") }}</v-subheader
    >
    <v-treeview
      dense
      :items="instructionsData"
      :open="instructionsOpen"
      class="treeview"
    ></v-treeview>
    <v-subheader
      v-if="employee.mentorshipHistory && employee.mentorshipHistory.length"
      class="subtitle mt-2 text-uppercase"
      >{{ $t("profile.subheader.mentorship") }}</v-subheader
    >
    <v-treeview
      dense
      :items="mentorshipsData"
      :open="mentorshipsOpen"
      class="treeview"
    >
      <template slot="label" slot-scope="props">
        <router-link :to="props.item.to" target="_blank" v-if="props.item.to">{{
          props.item.name
        }}</router-link>
        <span v-else>{{ props.item.name }}</span>
      </template>
    </v-treeview>

    <v-subheader
      v-if="employee.extTrainingEmployee && employee.extTrainingEmployee.length"
      class="subtitle mt-2 text-uppercase"
    >
      {{ $t("profile.subheader.extTrainings") }}
    </v-subheader>

    <v-treeview
      dense
      :items="trainingsData"
      :open="trainingsOpen"
      class="treeview"
    />

    <v-subheader
      v-if="employee.healthCheckResults && employee.healthCheckResults.length"
      class="subtitle mt-2 text-uppercase"
    >
      {{ $t("reports.healthChecks.title") }}
    </v-subheader>

    <v-treeview dense :items="healthChecksData" class="treeview" />
  </v-card>
</template>

<script>
import EmployeeHistoryQuery from "@/graphql/queries/employee/EmployeeHistory.gql";

export default {
  props: {
    id: {
      type: [Number, String],
      validator: function(value) {
        if (typeof value === "number") {
          return true;
        } else if (typeof value === "string") {
          return !isNaN(Number(value));
        }
        return false;
      },
      required: true,
    },
  },

  computed: {
    instructionsData() {
      const instructionsHistory =
        this.employee &&
        this.employee.instructionsHistory &&
        this.employee.instructionsHistory.map((i) => {
          let signature = i.versions[0].signatures.find(
            (s) =>
              s.type === "sign" &&
              !s.signatures.find((ss) => ss.type === "revoke")
          );

          let confirmation =
            signature &&
            signature.signatures.find(
              (s) =>
                s.type === "confirm" &&
                !s.signatures.find((ss) => ss.type === "revoke")
            );
          if (i.versions[0].markedAsRead) {
            signature = true;
            confirmation = true;
          }

          const name = i.versions[0].localizedFile.name;
          return {
            id: "i" + i.id,
            name: ` ${i.code} — ${name} ${
              i.isActive
                ? ""
                : "(" + this.$i18n.t(`instructions.inactive`) + ")"
            }`,
            group: signature
              ? confirmation
                ? "3.instructions.confirm.status"
                : "2.instructions.sign.status"
              : "1.instructions.pending.status",

            children: i.versions.map((v) => {
              if (v.markedAsRead) {
                v.signatures.push({
                  type: "marked",
                  date: v.markedAsRead.dateTime,
                  employee: v.markedAsRead.whoMarked,
                  signatures: [],
                  reason: v.markedAsRead.reason,
                });
              }

              return {
                id: "v" + v.id,
                name: `${v.revision} (${this.$options.filters.date(v.date)}
              ${this.$i18n.t(`instructions.column.dueInDays`)} ${v.dueInDays})
              ${
                v.rereadingInDays
                  ? `${this.$i18n.t(`instructions.column.reviewInDays`)}: ${
                      v.rereadingInDays
                    }`
                  : ""
              }`,
                children: this.mapSignatures(v.signatures, v.rereadingInDays),
              };
            }),
          };
        });
      const groups = instructionsHistory.reduce((g, i) => {
        (g[i.group] = g[i.group] || []).push(i);
        return g;
      }, {});

      return Object.keys(groups)
        .sort()
        .map((key) => ({
          id: key,
          name: this.$i18n.t(key.substring(2)),
          children: groups[key],
        }));
    },

    instructionsOpen() {
      const ids = [];
      this.instructionsData.forEach((g) => {
        ids.push(g.id);
        g.children.forEach((i) => {
          if (i.group !== "3.instructions.confirm.status") {
            ids.push(i.id);
            if (i.children && i.children.length > 0) {
              ids.push(...this.treeIds(i));
            }
          }
        });
      });
      return ids;
    },

    mentorshipsData() {
      return (
        (this.employee &&
          this.employee.mentorshipHistory &&
          this.employee.mentorshipHistory.map((m) => {
            const children = [];

            children.push(this.getMentorshipData(m));
            if (m.actions) {
              children.push(this.getActionsData(m));
            }
            if (m.feedbacks) {
              children.push(this.getFeedbackData(m));
            }

            return {
              id: "tag" + m.tag.id + m.id,
              name: `${this.$i18n.t(`profile.mentorship.column.tag`)} ${m.tag
                .description || m.tag.name}`,
              children,
            };
          })) ||
        []
      );
    },

    mentorshipsOpen() {
      const ids = [];
      this.mentorshipsData.forEach((m) => {
        ids.push(m.id);
        if (m.children && m.children.length > 0) {
          ids.push(...this.treeIds(m));
        }
      });
      return ids;
    },

    trainingsData() {
      if (
        !this.employee ||
        !this.employee.extTrainingEmployee ||
        !this.employee.extTrainingEmployee.length
      ) {
        return [];
      }

      const filteredTrainings = this.employee.extTrainingEmployee.filter(
        (t) => t.participated
      );

      const categoryGroups = {};

      filteredTrainings.forEach((t) => {
        const { categoryId, category } = t.extTraining;

        if (!categoryGroups[categoryId]) {
          categoryGroups[categoryId] = {
            name: category,
            items: [],
          };
        }

        categoryGroups[categoryId].items.push({
          id: t.externalTrainingId,
          name: `${t.extTraining.name} — ${this.$options.filters.date(
            t.extTraining.dateTimeOfOccurrence
          )}`,
          children: [
            {
              name: `${this.$i18n.t("extTrainings.column.educator")}: ${
                t.extTraining.educator
              }`,
            },
            {
              name: `${this.$i18n.t("extTrainings.column.location")}: ${
                t.extTraining.location
              }`,
            },
            {
              name: `${this.$i18n.t("extTrainings.durationMinutes")}: ${
                t.extTraining.durationMinutes
              }`,
            },
            {
              name: `${this.$i18n.t("extTrainings.dueInMonths")}: ${
                t.extTraining.dueInMonths === 0
                  ? "-"
                  : t.extTraining.dueInMonths
              }`,
            },
          ],
        });
      });

      return Object.entries(categoryGroups).map(
        ([categoryId, categoryGroup]) => ({
          id: "cat_" + categoryId,
          name: categoryGroup.name,
          children: categoryGroup.items,
        })
      );
    },

    trainingsOpen() {
      return this.trainingsData.map((c) => c.id);
    },

    healthChecksData() {
      if (
        !this.employee ||
        !this.employee.healthCheckResults ||
        !this.employee.healthCheckResults.length
      ) {
        return [];
      }

      return this.employee.healthCheckResults.map((h) => ({
        id: h.id,
        name: `${this.$options.filters.date(h.healthCheckOccurredAt)} — ${
          h.result
        }`,
        children: [
          {
            name: `${this.$i18n.t("healthCheck.result.suggestion")}: ${
              h.suggestion
            }`,
          },
          {
            name: `${this.$i18n.t(
              "healthCheck.result.nextHealthCheckDate"
            )}: ${this.$options.filters.date(h.nextHealthCheckDateTime)}`,
          },
        ],
      }));
    },
  },

  methods: {
    getActionsData(m) {
      return {
        id: "ma" + m.id,
        name: `${this.$i18n.t(`profile.mentorship.column.learningActions`)} ${
          m.actions.code
        }`,
        children: m.actions.assignedVersions.map((av) => {
          return {
            id: "av" + av.id,
            name: `${av.name.name} ${
              av.revision
            }(${this.$options.filters.dateTime(av.created)})`,
            children: av.content.map((c, index) => {
              return {
                id: "c" + c.assignedId + "_" + index,
                name: `${index + 1}. ${c.contentLocale.title}`,
                children: this.mapSignatures(c.signatures, null),
              };
            }),
          };
        }),
      };
    },
    getFeedbackData(m) {
      return {
        id: "mf" + m.id,
        name: `${this.$i18n.t(`profile.mentorship.column.feedback`)}`,
        children: m.feedbacks.map((f) => {
          return {
            id: "f" + f.id,
            name: `${this.$i18n.t(`profile.mentorship.feedback.stage`)} ${
              f.stage
            } ${f.code} ${f.revision}(${this.$options.filters.date(
              f.revisionCreated
            )}) ${
              f.menteeSubmittedForm
                ? this.$i18n.t("profile.mentorship.feedback.menteeSubmitted")
                : ""
            } ${
              f.menteeSubmittedForm
                ? this.$options.filters.date(f.menteeSubmittedForm.dateTime)
                : ""
            } ${
              f.mentorSubmittedForm
                ? this.$i18n.t("profile.mentorship.feedback.mentorSubmitted")
                : ""
            } ${
              f.mentorSubmittedForm
                ? this.$options.filters.date(f.mentorSubmittedForm.dateTime)
                : ""
            }`,
            to: {
              name: "mentorship-feedback-assigned",
              params: { id: f.assignedId },
              query: { language: this.$i18n.locale },
            },
          };
        }),
      };
    },
    getMentorshipData(m) {
      return {
        id: "m" + m.id,
        name: `${this.$i18n.t(
          `profile.mentorship.column.mentor`
        )} ${this.$options.filters.nameId(m.mentor)}
                ${this.$i18n.t(
                  `profile.mentorship.column.start`
                )} ${this.$options.filters.date(m.start)}
                ${this.$i18n.t(
                  `profile.mentorship.column.activeSince`
                )} ${this.$options.filters.date(m.activeSince)}`,
        children: this.mapSignatures(m.signatures, null),
      };
    },

    mapSignatures(signatures, rereadingInDays) {
      return (
        signatures &&
        signatures
          .sort((a, b) => (a.date < b.date ? 1 : -1))
          .map((s) => ({
            id: "s" + s.id,
            name: `${this.$i18n.t(
              `profile.history.signature.${s.type}`
            )} ${this.$options.filters.nameId(s.employee)}
          ${
            s.date
              ? this.$options.filters.dateTime(s.date)
              : this.$options.filters.dateTime(s.dateTime)
          }${s.reason ? ` — ${s.reason}` : ""}
            ${this.rereadingDaysToString(rereadingInDays, s)}`,
            children: this.mapSignatures(s.signatures, rereadingInDays),
          }))
      );
    },

    rereadingDaysToString(rereadingInDays, s) {
      return rereadingInDays && s.type === "sign"
        ? ` ${this.$i18n.t(
            `instructions.column.review`
          )}: ${this.$options.filters.dateTime(
            this.$moment(s.date)
              .add(rereadingInDays, "day")
              .format()
          )}`
        : "";
    },

    treeIds(tree) {
      const ids = [];
      if (tree.children && tree.children.length > 0) {
        tree.children.forEach((c) => {
          ids.push(c.id, ...this.treeIds(c));
        });
      }
      return ids;
    },
  },
  apollo: {
    employee: {
      query: EmployeeHistoryQuery,
      variables() {
        return {
          id: this.id,
        };
      },
    },
  },
};
</script>

<style>
.treeview {
  margin-left: 10px;
}
</style>
